import { useEffect, useRef, ReactNode } from 'react'
import { useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import posthog from 'posthog-js'

// Auto-records every signed-in, non-anonymous user via PostHog. Each PostHog
// session id is mirrored into Convex (`sessionRecordings`) so it shows up in
// /admin/sessions; the actual replay data lives in PostHog.
export function SessionCaptureProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth()
  const registerPosthogSession = useMutation(api.sessionRecordings.registerPosthogSession)
  const recordingActiveRef = useRef(false)
  const seenSessionIdsRef = useRef<Set<string>>(new Set())

  const eligible =
    !!import.meta.env.VITE_POSTHOG_KEY &&
    isAuthenticated &&
    !!user &&
    !user.isAnonymous

  useEffect(() => {
    if (!eligible) {
      if (recordingActiveRef.current) {
        posthog.stopSessionRecording()
        recordingActiveRef.current = false
      }
      return
    }

    posthog.startSessionRecording()
    recordingActiveRef.current = true

    const tryRegister = (sessionId: string | null | undefined) => {
      if (!sessionId || seenSessionIdsRef.current.has(sessionId)) return
      seenSessionIdsRef.current.add(sessionId)
      registerPosthogSession({ posthogSessionId: sessionId }).catch((e) => {
        seenSessionIdsRef.current.delete(sessionId)
        console.warn('[SessionCapture] Failed to register PostHog session id:', e)
      })
    }

    tryRegister(posthog.get_session_id())

    let unsubscribe: (() => void) | undefined
    const onSessionId = (posthog as any).onSessionId
    if (typeof onSessionId === 'function') {
      unsubscribe = onSessionId.call(posthog, (sessionId: string) => {
        tryRegister(sessionId)
      })
    } else {
      // Fallback poll for older posthog-js versions
      const handle = setInterval(() => tryRegister(posthog.get_session_id()), 2000)
      unsubscribe = () => clearInterval(handle)
    }

    return () => {
      unsubscribe?.()
    }
  }, [eligible, registerPosthogSession])

  return <>{children}</>
}
